const accessTokenKey = "partner-access-token";
export const partnerApiUrl = (import.meta.env.VITE_PARTNER_API_URL ?? "http://localhost:3006").replace(/\/$/, "");

export type PartnerSession = {
  userId: string;
  audience: "holdco_user" | "company_user" | "company_customer";
  holdcoId: string;
  grants: Array<{
    permissions: string[];
    scope: { type: string; companyId?: string; holdcoId?: string };
  }>;
};

let sessionCache: { accessToken: string; session: PartnerSession } | undefined;

export const getAccessToken = () => sessionStorage.getItem(accessTokenKey);

export async function signIn(apiUrl: string, values: {
  email: string;
  password: string;
  holdcoId?: string;
}) {
  const response = await fetch(`${apiUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(values),
  });
  const result = await response.json() as {
    accessToken?: string;
    expiresAt?: string;
    error?: string;
  };
  if (!response.ok || !result.accessToken) {
    throw new Error(result.error ?? "Sign-in failed");
  }
  sessionStorage.setItem(accessTokenKey, result.accessToken);
  sessionCache = undefined;
  return result.accessToken;
}

export async function signOut(apiUrl: string) {
  const accessToken = getAccessToken();
  sessionStorage.removeItem(accessTokenKey);
  sessionCache = undefined;
  if (!accessToken) return;
  await fetch(`${apiUrl}/auth/logout`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  }).catch(() => undefined);
}

export async function loadPartnerSession() {
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error("Authentication required");
  if (sessionCache?.accessToken === accessToken) return sessionCache.session;

  const response = await fetch(`${partnerApiUrl}/v1/session`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const result = await response.json() as { session?: PartnerSession; error?: string };
  if (!response.ok || !result.session) {
    if (response.status === 401) sessionStorage.removeItem(accessTokenKey);
    throw new Error(result.error ?? "Unable to load your session");
  }
  sessionCache = { accessToken, session: result.session };
  return result.session;
}

export function sessionPermissions(session: PartnerSession) {
  return new Set(session.grants.flatMap((grant) => grant.permissions));
}

export async function acceptInvitation(apiUrl: string, values: {
  invitationToken: string;
  password: string;
}) {
  const response = await fetch(`${apiUrl}/auth/invitations/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(values),
  });
  const result = await response.json() as { error?: string };
  if (!response.ok) throw new Error(result.error ?? "Invitation setup failed");
}
